import { useState } from 'react'
import {
  LEDGER_ACTIONS,
  recordLedgerEvent,
  remainingCents,
  type LedgerAction,
  type Payable,
} from '@core/payments'
import { formatCents, parseAmountToCents } from '@core/money'
import { Button, Card, ErrorNote, Field } from '@design/components'

/**
 * The things that happen to a bill besides paying it.
 *
 * Behind one tap rather than on the card itself, because "betaald" is what you
 * came to do and everything here is rare. But they are not exotic: a supplier
 * credits half an invoice, you dispute a delivery charge, a bill turns out to
 * have been sent twice. Without these, the only way to make such an item stop
 * nagging is to mark it paid — which quietly puts a lie in the permanent
 * record.
 *
 * Each one appends a fact; none updates a status (rule 4).
 */
export function LedgerActions({
  payable,
  userId,
  onDone,
}: {
  payable: Payable
  userId: string
  onDone: () => void
}) {
  const [open, setOpen] = useState(false)
  const [chosen, setChosen] = useState<LedgerAction | null>(null)
  const [amount, setAmount] = useState('')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const action = LEDGER_ACTIONS.find((a) => a.kind === chosen)

  // A resolved dispute is only offered when there is one to resolve, and vice
  // versa — rule 23's spirit: do not show what cannot apply.
  const available = LEDGER_ACTIONS.filter((a) => {
    if (a.kind === 'dispute_opened') return !payable.disputed
    if (a.kind === 'dispute_resolved') return payable.disputed
    if (a.kind === 'cancelled') return !payable.cancelled
    return true
  })

  async function submit() {
    if (!action) return
    let cents: number | undefined
    if (action.needsAmount) {
      const parsed = parseAmountToCents(amount)
      if (parsed === null || parsed <= 0) {
        setError('Vul een bedrag in.')
        return
      }
      cents = parsed
    }
    setBusy(true)
    setError('')
    try {
      await recordLedgerEvent({ payable, kind: action.kind, amountCents: cents, userId, note })
      setChosen(null)
      setOpen(false)
      setAmount('')
      setNote('')
      onDone()
    } catch {
      setError('Opslaan lukte niet.')
    }
    setBusy(false)
  }

  if (!open) {
    return (
      <div className="mt-3">
        <Button tone="quiet" onClick={() => setOpen(true)}>
          Iets anders…
        </Button>
      </div>
    )
  }

  if (action) {
    return (
      <Card className="mt-3">
        <p className="font-medium mb-1">{action.label}</p>
        <p className="text-sm text-muted mb-3">{action.hint}</p>

        {action.needsAmount && (
          <Field
            label="Bedrag"
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder={formatCents(remainingCents(payable)).replace(/[^\d.,]/g, '')}
            hint={`Openstaand: ${formatCents(remainingCents(payable))}`}
            autoFocus
          />
        )}

        <Field
          label="Notitie"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          hint="Waarom. Over een jaar weet je dit niet meer."
        />

        <ErrorNote>{error}</ErrorNote>

        <Button onClick={submit} disabled={busy}>
          {busy ? 'Bezig…' : action.label}
        </Button>
        <div className="mt-2">
          <Button tone="quiet" onClick={() => setChosen(null)}>
            Terug
          </Button>
        </div>
      </Card>
    )
  }

  return (
    <Card className="mt-3">
      <div className="space-y-2">
        {available.map((a) => (
          <button
            key={a.kind}
            onClick={() => setChosen(a.kind)}
            className="w-full text-left py-2 border-b border-line last:border-0"
          >
            <p className="font-medium">{a.label}</p>
            <p className="text-sm text-muted">{a.hint}</p>
          </button>
        ))}
      </div>
      <div className="mt-3">
        <Button tone="quiet" onClick={() => setOpen(false)}>
          Sluiten
        </Button>
      </div>
    </Card>
  )
}
